import React from 'react';
import { act } from 'react-dom/test-utils';
import { render, screen, waitFor } from '@testing-library/react';

import useBulkUserImport, { PHASES } from './useBulkUserImport';
import {
  consumeImportEvents,
  createBulkUserImport,
  fetchBulkUserImport,
} from '../services/bulkUserImportService';

jest.mock('../services/bulkUserImportService', () => ({
  consumeImportEvents: jest.fn().mockResolvedValue(undefined),
  createBulkUserImport: jest.fn(),
  fetchBulkUserImport: jest.fn(),
}));

// A probe rather than renderHook: @testing-library/react-hooks is not installed,
// and rendering the state is enough to assert on it. Same approach as
// useCreateUserForm.test.js.
//
// Everything below is synchronous `act` plus `waitFor`, and the poll runs on a
// real (short) timer. Async `act` deadlocks under this repo's React 16 + Jest 27
// pins — even `await act(async () => {})` never resolves — and Jest's fake
// timers fake the setImmediate that React's own flush depends on, so faking the
// clock is not an option either.
let hook;

const POLL_MS = 10;

function Probe() {
  hook = useBulkUserImport({ pollInterval: POLL_MS });

  return (
    <div>
      <span data-testid="phase">{hook.phase}</span>
      <span data-testid="status">{hook.importRecord?.status ?? ''}</span>
      <span data-testid="succeeded">
        {String(hook.importRecord?.succeeded ?? '')}
      </span>
      <span data-testid="error">{hook.uploadError ?? ''}</span>
      <span data-testid="uploading">{String(hook.isUploading)}</span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const file = { name: 'users.csv', size: 10, type: 'text/csv' };

const running = { id: 3, status: 'processing', isComplete: false };
const finished = {
  id: 3,
  status: 'success',
  isComplete: true,
  succeeded: 2,
  failed: 0,
  total: 2,
};

const startUpload = () => {
  act(() => {
    hook.upload(file);
  });
};

/** Long enough for several poll intervals to have come and gone. */
const settle = (intervals = 4) =>
  new Promise((resolve) => {
    setTimeout(resolve, POLL_MS * intervals);
  });

beforeEach(() => {
  createBulkUserImport.mockReset();
  fetchBulkUserImport.mockReset();
  consumeImportEvents.mockReset();
  consumeImportEvents.mockResolvedValue(undefined);
});

describe('management/Users useBulkUserImport', () => {
  it('starts on the upload step', () => {
    render(<Probe />);

    expect(at('phase')).toEqual(PHASES.upload);
    expect(at('status')).toEqual('');
  });

  it('moves to processing once the upload is accepted', async () => {
    createBulkUserImport.mockResolvedValue(running);
    render(<Probe />);

    startUpload();

    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));
    expect(createBulkUserImport).toHaveBeenCalledWith(file);
    expect(at('status')).toEqual('processing');
  });

  it('polls until the import is done, then shows the summary', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport
      .mockResolvedValueOnce(running)
      .mockResolvedValue(finished);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));

    await waitFor(() => expect(at('phase')).toEqual(PHASES.summary));
    expect(at('succeeded')).toEqual('2');
    // Polled more than once, so the summary came from a later poll rather than
    // from the upload response.
    expect(fetchBulkUserImport.mock.calls.length).toBeGreaterThan(1);
  });

  // The admin is about to read the outcome on the summary step, so the toast
  // for the same import must not also fire behind the wizard.
  it('spends the completion event when it shows the summary', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(finished);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.summary));

    expect(consumeImportEvents).toHaveBeenCalledWith(finished.id);
  });

  it('does not spend anything while the import is still running', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(running);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));
    await settle();

    expect(consumeImportEvents).not.toHaveBeenCalled();
  });

  it('stops polling once the import is done', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(finished);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.summary));

    const callsSoFar = fetchBulkUserImport.mock.calls.length;
    await settle();

    expect(fetchBulkUserImport).toHaveBeenCalledTimes(callsSoFar);
  });

  // A poll that fails is not an import that failed — it is still running on the
  // server, so the wizard asks again rather than giving up.
  it('keeps polling through a failed request', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport
      .mockRejectedValueOnce(new Error('network'))
      .mockResolvedValue(finished);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));

    await waitFor(() => expect(at('phase')).toEqual(PHASES.summary));
  });

  // Without an in-flight guard, a poll slower than the interval overlaps the
  // next one and two answers can land out of order, the older overwriting the
  // newer.
  it('does not start a second poll while one is still running', async () => {
    createBulkUserImport.mockResolvedValue(running);

    let release;
    fetchBulkUserImport.mockReturnValue(
      new Promise((resolve) => {
        release = () => resolve(finished);
      }),
    );
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));
    await settle(6);

    expect(fetchBulkUserImport).toHaveBeenCalledTimes(1);

    release();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.summary));
  });

  it("stays on the upload step and shows the server's reason for a rejected file", async () => {
    const error = new Error('unprocessable');
    error.fieldErrors = { original_file: ['must be smaller than 10 MB'] };
    createBulkUserImport.mockRejectedValue(error);
    render(<Probe />);

    startUpload();

    await waitFor(() =>
      expect(at('error')).toEqual('must be smaller than 10 MB'),
    );
    expect(at('phase')).toEqual(PHASES.upload);
    expect(at('uploading')).toEqual('false');
  });

  it('reports no reason when the failure carried none', async () => {
    createBulkUserImport.mockRejectedValue(new Error('boom'));
    render(<Probe />);

    startUpload();

    await waitFor(() => expect(at('uploading')).toEqual('false'));
    expect(at('phase')).toEqual(PHASES.upload);
    expect(at('error')).toEqual('');
  });

  it('returns to a blank upload step on reset', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(running);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));

    act(() => hook.reset());

    expect(at('phase')).toEqual(PHASES.upload);
    expect(at('status')).toEqual('');
  });

  it('stops polling once reset', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(running);
    render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));
    act(() => hook.reset());

    const callsSoFar = fetchBulkUserImport.mock.calls.length;
    await settle();

    expect(fetchBulkUserImport).toHaveBeenCalledTimes(callsSoFar);
  });

  // Closing the wizard mid-run must not leave a timer calling setState on an
  // unmounted component — the page behind it does its own watching.
  it('stops polling when it goes away', async () => {
    createBulkUserImport.mockResolvedValue(running);
    fetchBulkUserImport.mockResolvedValue(running);
    const { unmount } = render(<Probe />);

    startUpload();
    await waitFor(() => expect(at('phase')).toEqual(PHASES.processing));
    unmount();

    const callsSoFar = fetchBulkUserImport.mock.calls.length;
    await settle();

    expect(fetchBulkUserImport).toHaveBeenCalledTimes(callsSoFar);
  });
});
